// This component includes our custom plugin and rehype-sanitize (security for using custom components).
// Optionally could include clickable table of content that will scroll desired content of markdown into view.
// Optionally it can keep track of scrolled content and bubble that info upstream.
// Depending on project type that is provided it will sign resources on respective authentication server.

import React, {RefObject, SyntheticEvent} from "react";
import ReactMarkdown from "react-markdown";
import {connect} from "react-redux";
import rehypeSanitize, {defaultSchema} from "rehype-sanitize";
import {Schema} from "hast-util-sanitize";
import {Nullable} from "@data-force/app";
import rehypeMedia from "../../utils/rehypeMedia";
import {signResource} from "../../store/actions/job";
import {SignResourceAction} from "../../interfaces/store/actions/job";
import "./MarkdownMedia.scss";

interface OwnProps {
    children: string
    contentReadHandler?: Function
    projectType?: string
    readThroughContent?: boolean
    tocElements?: string[]
}

interface DefaultProps {
    contentReadHandler?: Function
    projectType: string
    readThroughContent: boolean
    tocElements: string[]
}

interface DispatchProps {
    signResource(payload: SyntheticEvent): SignResourceAction
}

type Props = OwnProps & DispatchProps;

const supportedMarkdownTocMap: { [key: string]: string } = {
    h1: "#",
    h2: "##",
};

class MarkdownMedia extends React.Component<Props> {
    // We changed default schema used to sanitize html to add our tags that we made with rehypeMedia plugin
    // also, we had to add few extra attributes for those tags
    private mediaAttributes: string[] = ["controls", "src", "style", "controlsList"];

    // content div ref, used to extract data needed for scroll validation
    private contentRef: RefObject<HTMLDivElement> = React.createRef();

    private defaultSchemaChanged: Schema = {
        ...defaultSchema,
        tagNames: [...defaultSchema.tagNames!, "video", "audio"],
        attributes: {
            ...defaultSchema.attributes,
            video: this.mediaAttributes,
            audio: this.mediaAttributes,
        },
    };

    // eslint-disable-next-line react/static-property-placement
    static defaultProps: DefaultProps = {
        contentReadHandler: () => {
        },
        projectType: "",
        readThroughContent: false,
        tocElements: [],
    };

    constructor(props: Props) {
        super(props);

        this.contentReadCheck = this.contentReadCheck.bind(this);
        this.getSearchRegex = this.getSearchRegex.bind(this);
        this.generateTableOfContent = this.generateTableOfContent.bind(this);
        this.contentScrollHandler = this.contentScrollHandler.bind(this);
        this.tocItemClickHandler = this.tocItemClickHandler.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const {children}: Props = this.props;

        if (prevProps.children !== children) {
            this.contentScrollHandler();
        }
    }

    /**
     * For given array of element types generates and returns unique regular expresion
     *
     * @param {string} tocElements
     * @return {RegExp}
     */
    getSearchRegex(tocElements: string[]): RegExp {
        let regexString: string = supportedMarkdownTocMap[tocElements[0]] || "";
        const elementsCount: number = tocElements.length;

        if (elementsCount > 1) {
            let currentIndex: number = 1;
            let currentRegExp: string = "";
            while (currentIndex < elementsCount) {
                currentRegExp = supportedMarkdownTocMap[tocElements[currentIndex]];
                if (currentRegExp) {
                    regexString += `${regexString ? "|" : ""}${currentRegExp}`;
                }
                currentIndex += 1;
            }
        }

        return new RegExp(`(^${regexString}) (.*)\\n`, "gm");
    }

    /**
     * Checks if markdown content has been scrolled to bottom,
     * and if so, calls provided handler to notify parent component.
     *
     * @param {boolean} scrolledToBottom
     */
    contentReadCheck(scrolledToBottom: boolean = false): void {
        if (scrolledToBottom) {
            this.props.contentReadHandler!();
        }
    }

    /**
     * Check on every scroll if markdown content has been scrolled to the bottom
     */
    contentScrollHandler(): void {
        if (this.contentRef.current) {
            const {clientHeight, scrollHeight, scrollTop}: HTMLDivElement = this.contentRef.current;
            // Changed tolerance from 1 to 5 pixels due to some browsers not recalculating scrollTop as expected on all resolutions.
            // The greater tolerance doesn't impact functionality, as there's a margin on the last element greater than 5px.
            if (scrollHeight - scrollTop - clientHeight <= 5) {
                this.contentReadCheck(true);
            }
        }
    }

    /**
     * Parse markdown content via regex and sets table of content element
     * when there are extracted h1 sections from regex.
     *
     * @return {JSX.Element | null}
     */
    generateTableOfContent(): JSX.Element | null {
        const {children, tocElements}: Props = this.props;

        const searchRegex: RegExp = this.getSearchRegex(tocElements!);
        // Regex that is provided with this method once executed will return result in following order
        // regexResult[0] - full match (e.g. "# First Heading\n")
        // regexResult[1] - first group match (e.g. "#")
        // regexResult[2] - second group match (e.g. "First Heading")
        // or null if no match was found.
        let regexResult: RegExpExecArray | null = searchRegex.exec(children);
        let tocItemIndex: number = 0;
        const tocItems: JSX.Element[] = [];

        while (regexResult !== null) {
            tocItems.push(
                <button
                    type="button"
                    className="df-table-of-content-item"
                    key={`df-table-of-content-item-${tocItemIndex}`}
                    onClick={this.tocItemClickHandler.bind(this, tocItemIndex, tocElements!)}
                >
                    {`${tocItemIndex + 1}. ${regexResult[2]}`}
                </button>,
            );
            tocItemIndex += 1;
            regexResult = searchRegex.exec(children);
        }

        return tocItems.length ? <div className="df-table-of-content">{tocItems}</div> : null;
    }

    /**
     * Finds and scrolls into view responding element that was clicked in table of content
     *
     * @param {number} index
     * @param {string[]} tocElements
     * @param {React.MouseEvent | React.KeyboardEvent} clickEvent
     */
    tocItemClickHandler(index: number, tocElements: string[], clickEvent: React.MouseEvent | React.KeyboardEvent): void {
        const tocContainerElement: Nullable<HTMLElement> = (clickEvent as React.MouseEvent).currentTarget?.parentElement;
        if (tocContainerElement) {
            const markdownContainerElement: HTMLElement = tocContainerElement?.parentElement?.children[1] as HTMLElement;
            const searchElements: NodeListOf<Element> | null = markdownContainerElement.querySelectorAll(tocElements.join(","));
            if (searchElements?.length) {
                (searchElements[index] as HTMLElement)?.scrollIntoView(
                    {
                        behavior: "smooth",
                        block: "center",
                    },
                );
            }
        }
    }

    render(): React.ReactElement {
        const {
            children,
            signResource: signResourceFn,
            projectType,
            readThroughContent,
        }: Props = this.props;

        return (
            <div className="df-markdown-with-toc-container">
                {this.generateTableOfContent()}
                <div
                    className="df-markdown-container"
                    ref={this.contentRef}
                    onScroll={readThroughContent ? this.contentScrollHandler : undefined}
                >
                    <ReactMarkdown
                        linkTarget="_blank"
                        // @ts-ignore
                        rehypePlugins={[
                            [rehypeSanitize, this.defaultSchemaChanged],
                            rehypeMedia.bind(this, signResourceFn, projectType!),
                        ]}
                    >
                        {children}
                    </ReactMarkdown>
                    {/* for accessibility purposes, allows tabbing to bottom of content */}
                    {/* this gets scroll to the bottom and allows validation to work */}
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <div tabIndex={0}/>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps: DispatchProps = {
    signResource,
};

export default connect(null, mapDispatchToProps)(MarkdownMedia);
